import { UserRole } from '@40seas/entities/users';
import { CurrentUser, DisplayOrganization } from '@40seas/frontend/libs/entities';
import { datadogRumProvider } from '@40seas/frontend/libs/providers';
import baseAxios, { AxiosError, AxiosInstance, AxiosRequestConfig } from 'axios';
import set from 'lodash/set';
import { EventTracker } from '../components/payments-core/src/analytics/eventTrackers/EventTracker';
import axiosRetry from 'axios-retry';

export class UsersService {
    private readonly axios: AxiosInstance;

    constructor(apiUrl: string, headers: Record<string, string>) {
        this.axios = baseAxios.create({
            baseURL: apiUrl,
            headers: headers,
        });

        axiosRetry(this.axios, {
            retries: 3,
            retryCondition: (error: AxiosError) => error.code === 'ERR_NETWORK',
            onRetry: (retryCount: number, error: AxiosError, requestConfig: AxiosRequestConfig) => {
                console.warn(`Retrying request to ${requestConfig.url} for the ${retryCount} time.`);
            },
            retryDelay: (retryCount) => retryCount * 100,
        });

        this.axios.interceptors.response.use(
            (response) => response,
            (error: AxiosError) => {
                const errorMessage = datadogRumProvider.getErrorMessage(error);

                // Don't report 404 errors to Datadog - these are expected client errors, not application bugs
                if (error.response?.status !== 404) {
                    datadogRumProvider.reportToDatadog(set(error, ['response', 'data', 'message'], errorMessage));
                }

                throw error;
            },
        );
    }

    async getOrRegister(token?: string): Promise<CurrentUser> {
        const currentUser = await this.getCurrent();

        if (currentUser.organizations.length) {
            return currentUser;
        }

        return this.register(token);
    }

    private async getCurrent(): Promise<CurrentUser> {
        const { data: user } = await this.axios.get<CurrentUser>('users/current', {
            params: { organizationName: getOrganizationName() },
        });
        return user;
    }

    async setRole(role: UserRole, organization?: DisplayOrganization) {
        const {
            data: { organizationName, tenant, viewMode },
        } = await this.axios.post('users/switch-role', { role, organization });

        return { organizationName, tenant, viewMode };
    }

    private async register(token?: string): Promise<CurrentUser> {
        const { data: user } = await this.axios.post<CurrentUser>(
            'users/register',
            {},
            token ? { headers: { '40seas-token': token } } : undefined,
        );

        return user;
    }

    initializeUserTrackers(user: CurrentUser) {
        EventTracker.Instance.setUser({
            email: user.email,
            name: user.fullName,
        });

        datadogRumProvider.setUser({
            email: user.email,
            id: user.auth0Id,
            name: user.fullName,
        });
    }
}

const getOrganizationName = () => {
    const params = window.location.pathname.split('/');
    const [_, organizationName] = params;
    return organizationName;
};
